import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import {
  Subject,
  filter,
  switchMap,
  takeUntil,
  merge,
  combineLatest,
  first,
  of,
} from 'rxjs';
import { delay, catchError } from 'rxjs/operators';

import { CommentAPIService, MemosAPIService } from 'shared/services';
import { AnnotationModel, Memo, Comment } from 'shared/models';
import { ToastService } from 'shared/ui';

import { EventBusService } from '../../services/event-bus.service';
import { BookService } from '../../services/book.service';
import { ReadingGroupsService } from '../../services/reading-groups.service';
import { OnlineReadingMemberService } from '../../services/online-reading-member.service';
import { MemoReadStatusService } from '../../services/memo-read-status.service';
import { UIStateService } from '../../services/ui-state.service';

import { HeaderMenuItem } from '../header/header.component';
import { RightPanelComponent } from '../right-panel/right-panel.component';
import { FooterMenuItem } from '../footer/footer.component';
import { AnnotationsV2Service } from '../../services/annotations-v2.service';
import { AnnotationSortType } from '../annotation-list/annotation-list.component';
import { ActivatedRoute } from '@angular/router';
import { BookType, parseBukURL, PagingMode, Address } from '@bukio/viewer';
import { getActiveIframes } from '../../utils/buk-viewer';

@Component({
  selector: 'viewer-reading-group-memo-list',
  templateUrl: './reading-group-memo-list.component.html',
  styleUrls: [
    '../annotation-list/annotation-list.component.scss',
    './reading-group-memo-list.component.scss',
  ],
})
export class ReadingGroupMemoListComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('scrollContainer', { static: true })
  private _scrollContainerElem!: ElementRef<HTMLDivElement>;
  @ViewChild('commentInput', { static: false })
  private _commentInputElem!: ElementRef<HTMLInputElement>;
  @ViewChild('mobileCommentInput', { static: false })
  private _mobileCommentInputElem!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('nicknameElement', { static: false })
  private _nicknameElement?: ElementRef<HTMLDivElement>;
  @ViewChild('mobileInputForm', { static: false })
  private _mobileInputFormElem?: ElementRef<HTMLDivElement>;

  @Input() isOwnedByUser = false;
  @Output() memoUpdated = new EventEmitter<Partial<Memo>>();
  @Output() replyCommentClick = new EventEmitter<{ memo: Memo }>();

  // 모바일 입력창 외부 클릭 감지
  @HostListener('document:click', ['$event'])
  public _onDocumentClick(event: MouseEvent): void {
    // 데스크탑일때는 무시
    if (window.innerWidth > 1200) {
      return;
    }

    // 프로그래밍적으로 생성된 이벤트는 무시 (스크롤 시 _closeAllMenus에서 생성되는 이벤트)
    if (!event.isTrusted) {
      return;
    }

    // 입력창이 활성화되어 있지 않으면 무시
    if (!this.isCommentInputValid) {
      return;
    }

    const target = event.target as HTMLElement;

    // 답글달기 버튼 클릭 확인
    // 클릭된 요소가 답글달기 버튼이거나 그 자식 요소인지 확인
    const replyButton = target.closest('button');
    if (replyButton && replyButton.textContent?.includes('답글달기')) {
      return;
    }

    // mobile-input-form 요소 확인
    if (!this._mobileInputFormElem?.nativeElement) {
      return;
    }

    const mobileInputForm = this._mobileInputFormElem.nativeElement;

    // 클릭된 요소가 mobile-input-form 내부에 있는지 확인
    if (mobileInputForm.contains(target)) {
      // comment-input-notification-popup 내부 클릭은 무시
      const notificationPopup = mobileInputForm.querySelector(
        '.comment-input-notification-popup'
      );
      if (notificationPopup && notificationPopup.contains(target)) {
        return;
      }
      // mobile-input-form 내부 클릭은 무시
      return;
    }

    // 외부 클릭 시 입력 비활성화
    this._disableCommentInput();
  }

  private _unsubscriber = new Subject<void>();

  public readonly _itemCountPerPage = 10;

  public _total = 0;
  public _memos?: Memo[];
  public _allMemos?: Memo[];
  private _currentGroupId?: number;
  public _commentId?: string; // 공유된 링크의 댓글 id
  public _mId?: string; // 공유된 링크의 메모 id
  public _page?: number; // 공유된 링크의 페이지

  public _currentPage = 1;

  private _load$ = new Subject<number>();

  public _onlineUserMap: { [userId: string]: boolean } = {};
  public _unreadMemoCount = 0;

  public _activeMemo?: Memo;

  private annotation?: AnnotationModel;
  public annotationText = '';
  public isCommentInputValid = false;
  public commentInputValue = '';
  public isCommentInputNotificationActive = false; // 댓글 or 답글 알림 활성화 여부
  public isCommentInputNotificationType: 'comment' | 'reply' = 'comment'; // 댓글 or 답글 알림 타입

  private _isCreatingMemo = false; // 댓글 생성 중 플래그 추가
  private _newlyCreatedMemoUrl?: string; // 새로 생성된 메모의 URL 저장
  public AnnotationSortType = AnnotationSortType; // 템플릿에서 사용하기 위해
  public _selectedSortType = AnnotationSortType.Position; // 기본값을 페이지순으로 변경
  public _selectedOrderBy: 'created_at' | 'position' | 'popular' = 'position'; // 타입에 'position' 추가 및 기본값 설정
  public _sortTypes = [
    AnnotationSortType.Position,
    AnnotationSortType.Date,
    AnnotationSortType.Popular,
  ];
  public _isUnderline = false;

  // 답글달기 관련 데이터
  public _isReplyCommentInputOpen = false;
  public _selectedMemo?: Memo;
  public _commentState?: 'comment' | 'subComment' | 'subSubComment';
  public _selectedComment?: any; // 답글의 답글을 위한 선택된 댓글
  public _selectedReply?: any; // 답글의 답글의 답글을 위한 선택된 답글

  // 닉네임 인덴트 캐시
  private _nicknameIndent = '0px';

  private _scrollTimeout?: any; // 스크롤 타이머 추가
  private _pageInfoChangeTimeouts: NodeJS.Timeout[] = []; // pageInfoChange 이벤트의 setTimeout 추적
  private _textareaScrollTimeouts: Map<HTMLTextAreaElement, NodeJS.Timeout> =
    new Map(); // textarea 스크롤 타이머
  public _isLoadingComment = false; // 댓글 등록 및 스크롤 로딩 상태
  private _previousAddress?: Address; // 이전 주소 저장
  private _isScrollMode = false; // 스크롤 모드 여부

  private _initialLoadCompleteEmitted = false;
  private _pendingScrollMemoId?: { memoId: string; source?: string };

  // iOS인지 판단하는 getter
  private get _isIOS(): boolean {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  constructor(
    private _eventBus: EventBusService,
    private _rightPanelComponent: RightPanelComponent,
    private _memosAPIService: MemosAPIService,
    private _bookService: BookService,
    private _readingGroupsService: ReadingGroupsService,
    private _onlineReadingMemberService: OnlineReadingMemberService,
    private _memoReadStatusService: MemoReadStatusService,
    private _toastService: ToastService,
    private _uiStateService: UIStateService,
    private _annotationService: AnnotationsV2Service,
    private _activatedRoute: ActivatedRoute,
    private _commentApiService: CommentAPIService,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // 공유된 링크의 댓글 id 가져오기
    const queryParams = this._activatedRoute.snapshot.queryParams;
    this._commentId = queryParams['commentId'];
    this._mId = queryParams['mId'];
    this._page = queryParams['page'];
    this._isUnderline = queryParams['isUnderline'] === 'true';

    // URL에서 위치 정보 추출 (예: /@ba1001/3/471-478?gId=21)
    const currentUrl = window.location.href;
    const urlMatch = currentUrl.match(/\/(\d+-\d+)(?:\?|$)/);
    let targetRange: [number, number] | null = null;
    if (urlMatch && !this._commentId && !this._mId) {
      // commentId나 mId가 없을 때만 위치 정보 사용
      const [startStr, endStr] = urlMatch[1].split('-');
      const start = parseInt(startStr);
      const end = parseInt(endStr);
      if (!isNaN(start) && !isNaN(end)) {
        targetRange = [start, end];
      }
    }

    combineLatest([
      this._bookService.book$,
      this._readingGroupsService.currentGroup$,
    ])
      .pipe(
        filter(([book, group]) => book !== null && group !== null),
        first(),
        takeUntil(this._unsubscriber)
      )
      .subscribe(() => {
        if (this._isUnderline) {
          this._emitInitialLoadComplete();
          return;
        }

        this._open();
      });

    this._eventBus
      .on('ContentsComponent:memoUpdated')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this._load$.next(this._currentPage);
      });

    this._eventBus
      .on('ContentsComponent:select')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        // 새로운 텍스트 선택 시 댓글 입력창 비활성화
        if (this.isCommentInputValid) {
          this.isCommentInputValid = false;
          this.isCommentInputNotificationActive = false;
          this.annotationText = '';
          this.commentInputValue = '';

          this._resetTextareaHeight();

          // 포커스 해제
          if (this._commentInputElem?.nativeElement) {
            this._commentInputElem.nativeElement.blur();
          }
          if (this._mobileCommentInputElem?.nativeElement) {
            this._mobileCommentInputElem.nativeElement.blur();
          }
        }

        // 본문 보기 되어있는 댓글 비활성화 처리
        this._eventBus.fire('ReadingModeMemoComponent:cancelViewText');
      });

    this._eventBus
      .on('ContentsComponent:commentClick')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ annotation }) => {
        // iOS인지 판단 및 데스크탑인지 판단 및 현재 댓글창이 열려있고 tab이 전체댓글인지 판단
        if (
          (this._isIOS || window.innerWidth >= 1200) &&
          this._rightPanelComponent.isOpened('comment')
        ) {
          this._commentState = undefined;
          this._selectedMemo = undefined;
          this._selectedComment = undefined;
          this._selectedReply = undefined;

          this.annotation = annotation;
          this.annotationText = annotation?.text || '';
          this.isCommentInputNotificationActive = true;
          this.isCommentInputNotificationType = 'comment';
          this.isCommentInputValid = true;
          this.commentInputValue = '';

          if (this._commentInputElem?.nativeElement) {
            this._commentInputElem.nativeElement.readOnly = false;
            this._commentInputElem.nativeElement.focus();
            this._resetTextareaHeight();
          }

          if (this._mobileCommentInputElem?.nativeElement) {
            this._mobileCommentInputElem.nativeElement.readOnly = false;
            this._mobileCommentInputElem.nativeElement.focus();
            this._resetTextareaHeight();
          }
        } else {
          setTimeout(() => {
            this._commentState = undefined;
            this._selectedMemo = undefined;
            this._selectedComment = undefined;
            this._selectedReply = undefined;

            this.annotation = annotation;
            this.annotationText = annotation?.text || '';
            this.isCommentInputNotificationActive = true;
            this.isCommentInputNotificationType = 'comment';
            this.isCommentInputValid = true;
            this.commentInputValue = '';

            if (this._commentInputElem?.nativeElement) {
              this._commentInputElem.nativeElement.readOnly = false;
              this._commentInputElem.nativeElement.focus();
              this._resetTextareaHeight();
            }

            if (this._mobileCommentInputElem?.nativeElement) {
              this._mobileCommentInputElem.nativeElement.readOnly = false;
              this._mobileCommentInputElem.nativeElement.focus();
              this._resetTextareaHeight();
            }
          }, 1000);
        }
      });

    merge(
      this._eventBus
        .on('HeaderComponent:menuClick')
        .pipe(filter(({ menu }) => menu === HeaderMenuItem.GroupMemos)),
      this._eventBus
        .on('FooterComponent:menuClick')
        .pipe(filter(({ menu }) => menu === FooterMenuItem.GroupMemos))
    )
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        if (!this._rightPanelComponent.isOpened('comment')) {
          this._open();
        }
      });

    this._eventBus
      .on('ContentsComponent:bookLoad')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        if (this._page) {
          this._currentPage = this._page;
        } else {
          this._currentPage = 1;
        }
      });

    // 페이지 이동 시 모바일 입력창 비활성화
    this._eventBus
      .on('ContentsComponent:addressChange')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this._disableCommentInput();
      });

    this._eventBus
      .on('ContentsComponent:pageClick')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this._disableCommentInput();
      });

    this._eventBus
      .on('RightPanelComponent:closed')
      .pipe(
        filter(({ activePanel }) => activePanel === 'comment'),
        takeUntil(this._unsubscriber)
      )
      .subscribe(() => {
        this._readingGroupsService.refreshGroupInfo();
        this._disableCommentInput();
      });

    this._eventBus
      .on('RightPanelComponent:opened')
      .pipe(
        filter(({ activePanel }) => activePanel === 'comment'),
        takeUntil(this._unsubscriber)
      )
      .subscribe(() => {
        const group = this._readingGroupsService.currentGroup;
        if (this._bookService.book && group) {
          this._unreadMemoCount = group.memo_unread_count;
          this._load$.next(this._unreadMemoCount > 0 ? 1 : this._currentPage);
        }
      });

    // 탭 변경 시 댓글 입력창 취소
    this._eventBus
      .on('RightPanelComponent:opened')
      .pipe(
        filter(({ activePanel }) => activePanel !== 'comment'),
        takeUntil(this._unsubscriber)
      )
      .subscribe(() => {
        // 댓글 입력창이 활성화되어 있으면 취소
        if (this.isCommentInputValid) {
          this._cancelCommentInput();
        }
      });

    // 스크롤 모드 여부 추적
    this._eventBus
      .on('ContentsComponent:settingsChanged')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ settings }) => {
        this._isScrollMode = settings.pagingMode.value === PagingMode.Scroll;
      });

    this._load$
      .pipe(
        switchMap(() => {
          return this._memosAPIService
            .get(this._bookService.book!.meta.bid, 0, 999, {
              group_id: this._readingGroupsService.currentGroup?.id
                ? this._readingGroupsService.currentGroup.id
                : this._currentGroupId,
              order_by: this._selectedOrderBy, // 기본 정렬 적용
            })
            .pipe(
              catchError(() =>
                of({
                  items: [] as Memo[],
                  total: 0,
                  offset: 0,
                  limit: 999,
                })
              )
            );
        })
      )
      .subscribe((response) => {
        if (response.offset > 0 && response.items.length === 0) {
          this._load$.next(1);
          return;
        }

        const firstMemoInPrevList = this._memos?.[0]?.id;

        this._allMemos = response.items;
        // 페이지네이션 제거: 모든 메모를 한 번에 표시
        this._memos = this._allMemos;
        this._total = response.total;
        this._currentPage = 1; // 페이지 정보는 유지하되 항상 1페이지로 설정

        if (this._page) {
          this._currentPage = this._page;
        }

        this._onlineUserMap = {};
        this._allMemos.forEach((m) => {
          this._onlineUserMap[m.author.id] =
            this._onlineReadingMemberService.isOnline(m.author.id);
        });

        if (
          firstMemoInPrevList &&
          firstMemoInPrevList !== this._memos?.[0]?.id
        ) {
          this._scrollContainerElem.nativeElement.scrollTop = 0;
        }

        // indicator 클릭으로 인한 pending scroll 처리
        if (this._pendingScrollMemoId) {
          const { memoId, source } = this._pendingScrollMemoId;
          this._pendingScrollMemoId = undefined;

          const memo = this._memos?.find((m) => m.id === memoId);
          if (memo) {
            this._eventBus.fire(
              'ReadingModeMemoComponent:seeDetailsButtonClick',
              {
                url: memo.url,
                id: memo.id,
                source: source || 'viewText',
              }
            );
          }

          setTimeout(() => {
            this._scrollToMemo(memoId, source);
          }, 100);
        } else if (this._commentId && !this._newlyCreatedMemoUrl) {
          setTimeout(() => {
            this._scrollToMemo(this._commentId!);
          }, 100);
        } else if (this._mId && !this._newlyCreatedMemoUrl) {
          setTimeout(() => {
            this._scrollToMemo(this._mId!);
          }, 100);
        } else if (targetRange) {
          // URL의 위치 정보로 메모 찾기
          setTimeout(() => {
            this._scrollToMemoByRange(targetRange!);
          }, 100);
        } else if (
          this._newlyCreatedMemoUrl &&
          this._memos &&
          this._memos.length > 0
        ) {
          // 새로 생성된 메모의 URL과 일치하는 메모 찾기
          const newlyCreatedMemo = this._memos.find(
            (memo) => memo.url === this._newlyCreatedMemoUrl
          );

          if (newlyCreatedMemo) {
            // 새로 생성된 메모로 스크롤
            setTimeout(() => {
              this._scrollToMemo(newlyCreatedMemo.id);
              this._newlyCreatedMemoUrl = undefined; // 스크롤 후 초기화
            }, 100);
          } else {
            // 메모를 찾지 못한 경우 로딩 종료
            this._isLoadingComment = false;
            this._newlyCreatedMemoUrl = undefined;
          }
        } else {
          // 새로 생성된 메모가 아닌 경우 로딩 종료
          if (this._isLoadingComment) {
            this._isLoadingComment = false;
          }
        }

        // targetRange 초기화해야지 댓글 달고난뒤 targetRange 페이지로 전환이 안됨
        targetRange = null;

        this._emitInitialLoadComplete();
      });

    // 사용자 차단 후 메모 리스트 새로고침
    this._eventBus
      .on('UserBlocked:refreshMemoLists')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this._refresh(); // 현재 페이지 다시 로드
      });

    // 그룹책이 바뀌는 경우 모든 댓글 다시 요청
    this._eventBus
      .on('ReadingModeDialogComponent:groupChanged')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ entered }) => {
        this._currentGroupId = entered ?? undefined;
        this._refresh();
      });

    // 메모로 스크롤 이벤트 구독
    this._eventBus
      .on('ReadingGroupMemoListComponent:scrollToMemo')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ memoId, source }) => {
        if (source === 'indicator') {
          if (this._rightPanelComponent.isOpened('comment')) {
            // 패널이 이미 열려있을 때: 기존 데이터로 즉시 스크롤
            // API 응답은 trackBy가 조용히 처리
            const memo = this._memos?.find((m) => m.id === memoId);
            if (memo) {
              this._eventBus.fire(
                'ReadingModeMemoComponent:seeDetailsButtonClick',
                { url: memo.url, id: memo.id, source: source || 'viewText' }
              );
            }
            this._scrollToMemo(memoId, source);
          } else {
            // 패널이 닫혀있을 때: 로딩 오버레이 표시 후 API 응답 시점에 스크롤
            this._isLoadingComment = true;
            this._pendingScrollMemoId = { memoId, source };
          }
        } else {
          // 그 외: 기존 방식 유지
          setTimeout(() => {
            const memo = this._memos?.find((m) => m.id === memoId);
            if (memo) {
              this._eventBus.fire(
                'ReadingModeMemoComponent:seeDetailsButtonClick',
                {
                  url: memo.url,
                  id: memo.id,
                  source: source || 'viewText',
                }
              );
            }

            this._scrollToMemo(memoId, source);
          }, 300);
        }
      });

    // 뷰어 페이지 전환 시 현재 페이지의 댓글로 자동 스크롤
    // pageTransitionEnd와 itemLoad 이벤트가 모두 발생한 후에만 pageInfoChange를 처리하도록 수정
    combineLatest([
      this._eventBus.on('ContentsComponent:pageInfoChange'),
      this._eventBus.on('ContentsComponent:pageTransitionEnd'),
      this._eventBus.on('ContentsComponent:itemLoad'),
    ])
      .pipe(
        filter(
          ([pageInfoChangeData]) =>
            this._rightPanelComponent.isOpened('comment') && // 전체 댓글 패널이 열려있을 때만
            this._selectedOrderBy === 'position' // 필터링이 페이지순으로 되어있을 때만
        ),
        // 추가 지연을 두어 iframe 내부 DOM 렌더링 완료 대기
        delay(100),
        takeUntil(this._unsubscriber)
      )
      .subscribe(([{ address, source }]) => {
        // 이전 setTimeout들 모두 취소
        this._pageInfoChangeTimeouts.forEach((timeout) =>
          clearTimeout(timeout)
        );
        this._pageInfoChangeTimeouts = [];

        // "본문보기"에서 온 경우 스크롤하지 않음
        if (source === 'viewText') {
          this._previousAddress = address;
          return;
        }

        // "본문보기 해제"에서 온 경우도 스크롤하지 않음
        if (source === 'viewTextCancel') {
          this._previousAddress = address;
          return;
        }

        // 공유하기를 통해 들어온 주소의 경우에는 페이지에 따른 스크롤하지 않음.
        if (address.query?.commentId || address.query?.mId) {
          this._previousAddress = address;
          return;
        }

        if (typeof address.anchor === 'string' && source !== 'searchResult') {
          this._previousAddress = address;
          return;
        }

        // 스크롤 모드에서 댓글 입력창이 활성화되어 있고 같은 페이지(iid 동일) 내에서 스크롤할 때는 댓글로 찾아가지 않음
        if (
          this._isScrollMode &&
          this.isCommentInputValid &&
          this._previousAddress &&
          this._previousAddress.iid === address.iid
        ) {
          this._previousAddress = address;
          return;
        }

        const isPdf = this._bookService.book?.meta.type === BookType.PDF;

        // epub의 경우
        if (!isPdf) {
          const viewerElement = document.querySelector(
            'viewer-contents'
          ) as HTMLElement;

          if (!viewerElement) {
            return;
          }

          // iframe의 contentDocument가 준비될 때까지 재시도하는 헬퍼 함수
          const waitForIframeReady = (
            iframe: HTMLIFrameElement,
            maxRetries: number = 10,
            retryDelay: number = 50
          ): Promise<Document | null> => {
            return new Promise((resolve) => {
              let retries = 0;

              const checkReady = (): void => {
                const doc = iframe.contentDocument;
                if (doc && doc.readyState === 'complete') {
                  resolve(doc);
                } else if (retries < maxRetries) {
                  retries++;
                  setTimeout(checkReady, retryDelay);
                } else {
                  resolve(doc || null);
                }
              };

              // iframe의 load 이벤트도 확인
              if (iframe.contentDocument?.readyState === 'complete') {
                resolve(iframe.contentDocument);
              } else {
                iframe.addEventListener('load', () => {
                  setTimeout(() => {
                    resolve(iframe.contentDocument);
                  }, 50);
                });
                checkReady();
              }
            });
          };

          const activeIframes = getActiveIframes(viewerElement);

          // 처음 렌더링 되는경우의 스크롤을 위한 로직
          if (!address.anchor) {
            const currentPageMemo = this._memos?.find((memo) => {
              const memoAddress = parseBukURL(memo.url);
              return memoAddress.iid === address.iid;
            });

            if (currentPageMemo) {
              this._scrollToMemoWithoutFocus(currentPageMemo.id);
            }
            return;
          }

          // 모든 iframe이 준비될 때까지 기다린 후 처리
          Promise.all(
            activeIframes.map((iframe) => waitForIframeReady(iframe))
          ).then((docs) => {
            docs.forEach((doc) => {
              if (!doc) {
                return;
              }

              let isPass = false;

              doc.querySelectorAll('.bukh-color-momo-m').forEach((element) => {
                const timeout = setTimeout(() => {
                  // 배열에서 제거
                  const index = this._pageInfoChangeTimeouts.indexOf(timeout);
                  if (index > -1) {
                    this._pageInfoChangeTimeouts.splice(index, 1);
                  }

                  if (isPass) {
                    return;
                  }

                  const rect = element.getBoundingClientRect();

                  if (
                    rect.x > 0 &&
                    rect.x < viewerElement.clientWidth &&
                    rect.y > 0 &&
                    rect.y < viewerElement.clientHeight
                  ) {
                    const highlightId = element.getAttribute('highlight-id');

                    if (!highlightId) {
                      return;
                    }

                    const highlightAnchor = highlightId.split('.')[0];

                    const filteredMemos = this._memos?.filter((memo) => {
                      if (
                        memo.url.split('/')[memo.url.split('/').length - 1] ===
                        highlightAnchor
                      ) {
                        return true;
                      }
                      return false;
                    });

                    if (filteredMemos && filteredMemos.length > 0) {
                      isPass = true;

                      setTimeout(() => {
                        this._scrollToMemoWithoutFocus(filteredMemos[0].id);
                      }, 100);
                    }
                  }
                }, 1000);

                this._pageInfoChangeTimeouts.push(timeout);
              });
            });
          });
        } else {
          // pdf인 경우 현재 페이지의 메모 찾기
          const currentPageMemo = this._memos?.find((memo) => {
            const memoAddress = parseBukURL(memo.url);
            return memoAddress.iid === address.iid;
          });

          if (currentPageMemo) {
            this._scrollToMemoWithoutFocus(currentPageMemo.id);
          }
        }

        // 주소 업데이트
        this._previousAddress = address;
      });
  }

  ngAfterViewInit(): void {
    // 초기 닉네임 인덴트 업데이트
    setTimeout(() => {
      this._updateNicknameIndent();
    }, 0);

    if (this._scrollContainerElem) {
      this._scrollContainerElem.nativeElement.addEventListener('scroll', () => {
        this._closeAllMenus();

        // 스크롤 중일 때 클래스 추가
        this._scrollContainerElem.nativeElement.classList.add('is-scrolling');

        // 기존 타이머가 있으면 제거
        if (this._scrollTimeout) {
          clearTimeout(this._scrollTimeout);
        }

        // 스크롤이 멈춘 후 300ms 후에 클래스 제거
        this._scrollTimeout = setTimeout(() => {
          this._scrollContainerElem.nativeElement.classList.remove(
            'is-scrolling'
          );
        }, 300);
      });

      // 댓글 패널 내에서 화살표 키로 스크롤 방지
      this._scrollContainerElem.nativeElement.addEventListener(
        'keydown',
        (event: KeyboardEvent) => {
          // 화살표 키인 경우 기본 동작(스크롤) 방지
          if (
            [
              'ArrowUp',
              'ArrowDown',
              'ArrowLeft',
              'ArrowRight',
              'Up',
              'Down',
              'Left',
              'Right',
              'PageUp',
              'PageDown',
            ].includes(event.key)
          ) {
            // textarea나 input 요소에 포커스가 있는 경우는 제외
            const target = event.target as HTMLElement;
            if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') {
              return;
            }
            event.preventDefault();
          }
        },
        true
      ); // capture phase에서 처리하여 하위 요소로 전파 방지
    }
  }

  ngOnDestroy(): void {
    // 타이머 정리
    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
    }

    // pageInfoChange 타이머들도 정리
    this._pageInfoChangeTimeouts.forEach((timeout) => clearTimeout(timeout));
    this._pageInfoChangeTimeouts = [];

    // textarea 스크롤 타이머 정리
    this._textareaScrollTimeouts.forEach((timeout) => clearTimeout(timeout));
    this._textareaScrollTimeouts.clear();

    this._unsubscriber.next();
    this._unsubscriber.complete();
  }

  public _onCommentInputKeyDown(event: any): void {
    if (event.shiftKey) {
      return;
    }

    event.preventDefault();

    // 이미 댓글 생성 중이면 무시
    if (this._isCreatingMemo) {
      return;
    }

    if (this.commentInputValue.trim() === '') {
      return;
    }

    if (!this.annotation) {
      return;
    }

    // 댓글 생성 시작
    this._isCreatingMemo = true;
    this._isLoadingComment = true; // 로딩 시작
    // 새로 생성될 메모의 URL 저장
    this._newlyCreatedMemoUrl = this.annotation.url;

    this._annotationService
      .createMemo(this.annotation, this.commentInputValue.trim(), false)
      .subscribe({
        next: () => {
          this._disableCommentInput();
          this._eventBus.fire('ContentsComponent:memoUpdated');
        },
        error: () => {
          // 에러 발생 시에도 플래그 해제
          this._isCreatingMemo = false;
          this._isLoadingComment = false; // 로딩 종료
          this._newlyCreatedMemoUrl = undefined;
        },
        complete: () => {
          // 완료 시 플래그 해제
          this._isCreatingMemo = false;
        },
      });

    return;
  }

  // 모바일에서 댓글 입력창 제출 버튼
  public _onMobileCommentInputSubmit(): void {
    if (this.commentInputValue.trim() === '') {
      return;
    }

    // 댓글 입력의 경우
    if (!this._isReplyCommentInputOpen) {
      if (!this.annotation) {
        return;
      }

      // 댓글 생성 시작
      this._isCreatingMemo = true;
      this._isLoadingComment = true; // 로딩 시작
      // 새로 생성될 메모의 URL 저장
      this._newlyCreatedMemoUrl = this.annotation.url;

      this._annotationService
        .createMemo(this.annotation, this.commentInputValue.trim(), false)
        .subscribe({
          next: () => {
            this._disableCommentInput();
            this._eventBus.fire('ContentsComponent:memoUpdated');
          },
          error: () => {
            // 에러 발생 시에도 플래그 해제
            this._isCreatingMemo = false;
            this._isLoadingComment = false; // 로딩 종료
            this._newlyCreatedMemoUrl = undefined;
          },
          complete: () => {
            // 완료 시 플래그 해제
            this._isCreatingMemo = false;
          },
        });
    } else {
      if (!this._selectedMemo) {
        return;
      }

      // commentState에 따라 분기 처리
      if (this._commentState === 'comment') {
        // 첫 번째 답글 생성
        // 메모 작성자에게 멘션 (자신의 메모가 아닌 경우만)
        const isMine = this._selectedMemo.is_mine === 1;
        const mentionUserId = isMine ? '' : this._selectedMemo.author.id;

        this._commentApiService
          .createComment(
            this._selectedMemo.id,
            this.commentInputValue.trim(),
            mentionUserId
          )
          .subscribe(
            (result) => {
              this.commentInputValue = '';
              this.isCommentInputValid = false;
              this.isCommentInputNotificationActive = false;
              this.annotationText = '';
              this._isReplyCommentInputOpen = false;

              this._resetTextareaHeight();

              // textarea에서 포커스 제거
              if (this._commentInputElem?.nativeElement) {
                this._commentInputElem.nativeElement.blur();
              }
              if (this._mobileCommentInputElem?.nativeElement) {
                this._mobileCommentInputElem.nativeElement.blur();
              }

              // reading-mode-memo 컴포넌트에 답글 전달
              this._eventBus.fire(
                'ReadingGroupMemoListComponent:replyCommentAdded',
                {
                  memoId: this._selectedMemo!.id,
                  comment: {
                    ...result,
                    isSubReplyOpened: 0,
                  },
                }
              );
            },
            (error) => {
              if (error.status === 404) {
                this._toastService.openWarning('존재하지 않는 댓글입니다.');
              }
            }
          );
      } else if (this._commentState === 'subComment' && this._selectedComment) {
        // 답글의 답글 생성
        const isMine = this._selectedComment.is_mine === 1;
        const authorId = isMine ? '' : this._selectedComment.author.id;

        this._commentApiService
          .createReply(
            this._selectedMemo.id,
            this.commentInputValue.trim(),
            authorId,
            this._selectedComment.id
          )
          .subscribe(
            (result) => {
              this.commentInputValue = '';
              this.isCommentInputValid = false;
              this._isReplyCommentInputOpen = false;

              this._resetTextareaHeight();

              // textarea에서 포커스 제거
              if (this._commentInputElem?.nativeElement) {
                this._commentInputElem.nativeElement.blur();
              }
              if (this._mobileCommentInputElem?.nativeElement) {
                this._mobileCommentInputElem.nativeElement.blur();
              }

              // reading-mode-memo 컴포넌트에 답글의 답글 전달
              this._eventBus.fire(
                'ReadingGroupMemoListComponent:subReplyCommentAdded',
                {
                  memoId: this._selectedMemo!.id,
                  commentId: this._selectedComment.id,
                  reply: {
                    ...result,
                    isSubReplyOpened: 0,
                  },
                }
              );
            },
            (error) => {
              if (error.status === 404) {
                this._toastService.openWarning('존재하지 않는 댓글입니다.');
              }
            }
          );
      } else if (
        this._commentState === 'subSubComment' &&
        this._selectedReply
      ) {
        // 답글의 답글의 답글 생성
        const isMine = this._selectedReply.is_mine === 1;
        const authorId = isMine ? '' : this._selectedReply.author.id;

        this._commentApiService
          .createReply(
            this._selectedMemo.id,
            this.commentInputValue.trim(),
            authorId,
            this._selectedComment.id
          )
          .subscribe(
            (result) => {
              this.commentInputValue = '';
              this.isCommentInputValid = false;
              this._isReplyCommentInputOpen = false;

              this._resetTextareaHeight();

              // textarea에서 포커스 제거
              if (this._commentInputElem?.nativeElement) {
                this._commentInputElem.nativeElement.blur();
              }
              if (this._mobileCommentInputElem?.nativeElement) {
                this._mobileCommentInputElem.nativeElement.blur();
              }

              // reading-mode-memo 컴포넌트에 답글의 답글의 답글 전달
              this._eventBus.fire(
                'ReadingGroupMemoListComponent:subSubReplyCommentAdded',
                {
                  memoId: this._selectedMemo!.id,
                  commentId: this._selectedComment?.id,
                  replyId: this._selectedReply.id,
                  reply: {
                    ...result,
                    isSubReplyOpened: 0,
                  },
                }
              );
            },
            (error) => {
              if (error.status === 404) {
                this._toastService.openWarning('존재하지 않는 댓글입니다.');
              }
            }
          );
      } else {
        // 답글 수정
        this._commentApiService
          .updateComment(
            this._selectedMemo.id,
            this.commentInputValue.trim(),
            '',
            this._selectedReply.id
          )
          .subscribe((result) => {
            this.commentInputValue = '';
            this.isCommentInputValid = false;
            this._isReplyCommentInputOpen = false;

            this._resetTextareaHeight();

            // textarea에서 포커스 제거
            if (this._commentInputElem?.nativeElement) {
              this._commentInputElem.nativeElement.blur();
            }
            if (this._mobileCommentInputElem?.nativeElement) {
              this._mobileCommentInputElem.nativeElement.blur();
            }

            this._eventBus.fire(
              'ReadingGroupMemoListComponent:subReplyCommentChanged',
              {
                memo: this._selectedMemo!,
                originalReply: this._selectedReply!,
                reply: {
                  ...result,
                },
              }
            );
          });
      }
    }
  }

  private _resetTextareaHeight(): void {
    if (this._commentInputElem?.nativeElement) {
      const textarea = this._commentInputElem.nativeElement;
      textarea.style.height = '22px';
    }
    if (this._mobileCommentInputElem?.nativeElement) {
      const textarea = this._mobileCommentInputElem.nativeElement;
      textarea.style.height = '14px';
    }
  }

  public _onCommentInputChange(): void {
    // 데스크톱용 textarea 높이 조절
    if (this._commentInputElem?.nativeElement) {
      const textarea = this._commentInputElem.nativeElement;
      // 높이를 내용에 맞게 자동 조절
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      // 한 줄 높이 계산 (기본 22px)
      const lineHeight = 22;
      const maxHeight = lineHeight * 5; // 5줄 최대 높이
      // 내용이 비어있거나 한 줄일 때는 22px, 최대 5줄까지
      const newHeight =
        scrollHeight <= 22 ? 22 : Math.min(scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    }

    // 모바일용 textarea 높이 조절
    if (this._mobileCommentInputElem?.nativeElement) {
      const textarea = this._mobileCommentInputElem.nativeElement;
      // 높이를 내용에 맞게 자동 조절
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      // 한 줄 높이 계산 (기본 22px)
      const lineHeight = 14;
      const maxHeight = lineHeight * 5; // 5줄 최대 높이
      // 내용이 비어있거나 한 줄일 때는 22px, 최대 5줄까지
      const newHeight =
        scrollHeight <= 14 ? 14 : Math.min(scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }

  // textarea 내에서 화살표 키 사용 시 뷰어 페이지 이동 방지
  public _onTextareaArrowKey(event: any): void {
    event.stopPropagation();
  }

  // textarea 스크롤 이벤트 핸들러
  public _onTextareaScroll(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;

    // 스크롤 중일 때 클래스 추가
    textarea.classList.add('is-scrolling');

    // 기존 타이머가 있으면 제거
    const existingTimeout = this._textareaScrollTimeouts.get(textarea);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // 스크롤이 멈춘 후 300ms 후에 클래스 제거
    const timeout = setTimeout(() => {
      textarea.classList.remove('is-scrolling');
      this._textareaScrollTimeouts.delete(textarea);
    }, 300);

    this._textareaScrollTimeouts.set(textarea, timeout);
  }

  // Command/Ctrl + Enter로 저장 처리
  public _onTextareaKeyDown(event: KeyboardEvent): void {
    // ESC 키 처리: 입력 취소
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this._cancelCommentInput();
      return;
    }

    // Command + Enter (Mac) 또는 Ctrl + Enter (Windows/Linux)
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      this._onCommentInputKeyDown(event);
    }
  }

  // 모든 열린 메뉴를 닫는 메서드
  private _closeAllMenus(): void {
    const backdropClickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      clientX: 0,
      clientY: 0,
    });

    // document.body에 클릭 이벤트를 발생시켜 CDK의 backdrop 클릭 처리 로직을 트리거
    document.body.dispatchEvent(backdropClickEvent);

    // 또는 더 직접적으로 overlay backdrop을 찾아서 클릭 이벤트 발생
    const overlayBackdrop = document.querySelector('.cdk-overlay-backdrop');
    if (overlayBackdrop) {
      overlayBackdrop.dispatchEvent(backdropClickEvent);
    }
  }

  private _emitInitialLoadComplete(): void {
    if (this._initialLoadCompleteEmitted) {
      return;
    }
    this._initialLoadCompleteEmitted = true;
    this._eventBus.fire('ReadingGroupMemoListComponent:initialLoadComplete');
  }

  private _open(): void {
    const group = this._readingGroupsService.currentGroup;

    if (!this._bookService.book || !group) {
      // combineLatest로 들어온 경우와 달리 여기서만 막히면 풀스크린이 영구 표시되지 않도록
      this._emitInitialLoadComplete();
      return;
    }

    this._rightPanelComponent.open('comment');

    this._unreadMemoCount = group.memo_unread_count;

    /**
     * 안 읽은 메모가 있다면 1 페이지를 불러옴
     * 안 읽은 메모가 없다면 이전에 보던 페이지를 불러옴 (내가 추가, 수정 또는 삭제한 메모가 있을 수 있기 때문에 새로 불러와야함)
     */
    this._load$.next(this._unreadMemoCount > 0 ? 1 : this._currentPage);
  }

  private _close(): void {
    if (!this._rightPanelComponent.isOpened('comment')) {
      return;
    }

    this._rightPanelComponent.close(false);
  }

  // 메모(댓글) 목록 다시 불러오는 함수
  public _refresh(): void {
    this._load$.next(this._currentPage);
  }

  public _onPageChanged(page: number): void {
    // 페이지네이션 제거로 인해 더 이상 사용하지 않음
    // 메서드는 유지하되 동작하지 않음
    this._currentPage = page;
  }

  public _onMemoRead(memo: Memo): void {
    !memo.is_read && this._memoReadStatusService.read(memo.id);
  }

  public _onMemoClick(memo: Memo): void {
    if (!this._uiStateService.canUseMemoSidebar()) {
      this._close();
    }

    this._activeMemo = memo;

    this._eventBus.fire('ReadingGroupMemoListComponent:memoClick', {
      url: memo.url,
    });
  }

  public _onMemoUpdated(memo: Memo, updated: Partial<Memo>): void {
    Object.keys(updated).forEach((key) => {
      (memo as any)[key] = updated[key as keyof Memo];
    });
  }

  public _onSortTypeChange(): void {
    // 한글 값을 영어로 변환하여 _selectedOrderBy 업데이트
    const orderByMap: { [key: string]: 'created_at' | 'position' | 'popular' } =
      {
        [AnnotationSortType.Position]: 'position',
        [AnnotationSortType.Date]: 'created_at',
        [AnnotationSortType.Popular]: 'popular',
      };

    this._selectedOrderBy = orderByMap[this._selectedSortType] as
      | 'created_at'
      | 'position'
      | 'popular';

    // 페이지네이션 제거: 모든 데이터를 한 번에 가져오기
    this._memosAPIService
      .get(this._bookService.book!.meta.bid, 0, 999, {
        group_id: this._readingGroupsService.currentGroup!.id,
        order_by: this._selectedOrderBy,
      })
      .subscribe((response) => {
        this._allMemos = response.items;
        this._memos = this._allMemos; // 모든 메모 표시
        this._total = response.total;
        this._currentPage = 1;
        this._resetTextareaHeight();
      });
  }

  public _cancelCommentInput(): void {
    // 답글 입력 취소 이벤트 발생
    if (this._isReplyCommentInputOpen) {
      this._eventBus.fire(
        'ReadingGroupMemoListComponent:replyInputCancelled' as any
      );
    }
    // 댓글 입력 취소 이벤트 발생
    else if (this.isCommentInputValid) {
      this._eventBus.fire('ContentsComponent:commentInputCancelled' as any);
    }

    this.isCommentInputValid = false;
    this.isCommentInputNotificationActive = false;
    this.annotationText = '';
    this.commentInputValue = '';
    this._resetTextareaHeight();

    // 하이라이트 제거 이벤트 발생
    this._eventBus.fire(
      'ReadingGroupMemoListComponent:clearTemporaryHighlight'
    );
  }

  // 특정 메모로 스크롤 이동하는 메서드 추가
  private _scrollToMemo(memoId: string, source?: string): void {
    // 해당 메모 요소 찾기 (data-memo-id 속성 사용)
    const memoElement = document.querySelector(
      `viewer-reading-mode-memo[data-memo-id="${memoId}"]`
    );

    if (memoElement && this._scrollContainerElem) {
      const scrollContainer = this._scrollContainerElem.nativeElement;

      // DOM이 완전히 렌더링될 때까지 대기하여 스크롤 위치 계산 정확도 향상
      setTimeout(() => {
        // 컨테이너와 메모 요소의 위치 계산
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = memoElement.getBoundingClientRect();

        // 컨테이너의 현재 스크롤 위치
        const currentScrollTop = scrollContainer.scrollTop;

        // 메모를 컨테이너 최상단에 위치시키기 위한 스크롤 계산
        const elementOffsetFromContainer = elementRect.top - containerRect.top;
        const targetScrollTop = currentScrollTop + elementOffsetFromContainer;

        // 스크롤 시작 직전에 로딩 완료 처리
        if (this._isLoadingComment) {
          this._isLoadingComment = false;
        }

        // 스크롤을 위해 loading-disabled 클래스 제거
        // (overflow: hidden 상태에서는 스크롤이 불가능하므로)
        scrollContainer.classList.remove('loading-disabled');

        // 이미 목표 위치에 있는지 확인
        const currentPos = scrollContainer.scrollTop;
        const distanceToTarget = Math.abs(currentPos - targetScrollTop);

        if (distanceToTarget >= 1) {
          // 부드러운 스크롤로 이동
          scrollContainer.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth',
          });
        }

        // 포커스 효과를 위한 클래스 추가
        memoElement.classList.add('focused-memo');

        const memo = this._memos?.find((m) => m.id === memoId);

        if (memo) {
          this._eventBus.fire(
            'ReadingModeMemoComponent:seeDetailsButtonClick',
            {
              url: memo.url,
              id: memo.id,
            }
          );
        }
      }, 100);
    } else {
      // 메모 요소를 찾지 못한 경우 로딩 종료
      if (this._isLoadingComment) {
        this._isLoadingComment = false;
      }
    }
  }

  // 스크롤만 하는 메서드 (포커스 없이)
  private _scrollToMemoWithoutFocus(memoId: string): void {
    // 해당 메모 요소 찾기 (data-memo-id 속성 사용)
    const memoElement = document.querySelector(
      `viewer-reading-mode-memo[data-memo-id="${memoId}"]`
    );

    if (memoElement && this._scrollContainerElem) {
      const scrollContainer = this._scrollContainerElem.nativeElement;

      // 컨테이너와 메모 요소의 위치 계산
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = memoElement.getBoundingClientRect();

      // 컨테이너의 현재 스크롤 위치
      const currentScrollTop = scrollContainer.scrollTop;

      // 메모를 컨테이너 상단에 위치시키기 위한 스크롤 계산
      const elementOffsetFromContainer = elementRect.top - containerRect.top;
      const targetScrollTop = currentScrollTop + elementOffsetFromContainer;

      // 부드러운 스크롤로 이동
      scrollContainer.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth',
      });
    }
  }

  // 위치 정보로 메모를 찾아 스크롤하는 메서드 추가
  private _scrollToMemoByRange(range: [number, number]): void {
    if (!this._memos || this._memos.length === 0) {
      return;
    }

    if (!range) {
      return;
    }

    // 위치 정보와 일치하는 메모 찾기
    const targetMemo = this._memos.find((memo) => {
      const memoAddress = parseBukURL(memo.url);
      if (!memoAddress.range) {
        return false;
      }

      const [memoStartStr, memoEndStr] = memoAddress.range.split('-');
      const memoStart = parseInt(memoStartStr);
      const memoEnd = parseInt(memoEndStr);

      // 범위가 겹치는지 확인 (471-478과 메모의 범위가 겹치는지)
      return (
        (range[0] >= memoStart && range[0] <= memoEnd) ||
        (range[1] >= memoStart && range[1] <= memoEnd) ||
        (memoStart >= range[0] && memoStart <= range[1]) ||
        (memoEnd >= range[0] && memoEnd <= range[1])
      );
    });

    if (targetMemo) {
      this._scrollToMemo(targetMemo.id);
    }
  }

  // 답글달기 클릭 시 모바일 입력 폼 활성화
  public _onReplyCommentClick(data: {
    memo: Memo;
    comment: Memo;
    commentState: 'comment' | 'subComment' | 'subSubComment';
    reply?: Memo;
  }): void {
    this.commentInputValue = '';
    this.isCommentInputValid = true;
    this._isReplyCommentInputOpen = true;
    this.isCommentInputNotificationType = 'reply';
    this.isCommentInputNotificationActive = true;

    this._selectedMemo = data.memo;
    this._selectedComment = data.comment;
    this._commentState = data.commentState;
    this._selectedReply = data.reply;

    // 답글 입력 시작 이벤트 발생
    const mentionTarget = this._getMentionTarget();
    this._eventBus.fire(
      'ReadingGroupMemoListComponent:replyInputStarted' as any,
      {
        nickname: mentionTarget?.nickname || '',
      }
    );

    if (this._mobileCommentInputElem?.nativeElement) {
      this._mobileCommentInputElem.nativeElement.readOnly = false;
      this._mobileCommentInputElem.nativeElement.focus();
      this._resetTextareaHeight();

      // 닉네임 인덴트 업데이트를 다음 변경 감지 사이클에서 실행
      setTimeout(() => {
        this._updateNicknameIndent();
      }, 0);
    }
  }

  // 모바일에서 답글 수정하기 클릭 시 모바일 입력 폼 활성화
  public _onSubReplyCommentChanged(data: { memo: Memo; reply: Comment }): void {
    this._commentState = undefined;
    this._selectedComment = undefined;

    // 답글 내용을 가져와서 HTML 태그 제거 후 설정
    const content = data.reply.content || '';
    // HTML 태그 제거 및 줄바꿈 유지
    const textContent = content
      .replace(/<br\s*\/?>/gi, '\n') // <br> 태그를 줄바꿈으로 변환
      .replace(/<\/p>/gi, '\n') // </p> 태그를 줄바꿈으로 변환
      .replace(/<[^>]*>/g, '') // 나머지 HTML 태그 제거
      .replace(/&nbsp;/g, ' ') // &nbsp;를 공백으로 변환
      .replace(/&amp;/g, '&') // &amp;를 &로 변환
      .replace(/&lt;/g, '<') // &lt;를 <로 변환
      .replace(/&gt;/g, '>') // &gt;를 >로 변환
      .replace(/&quot;/g, '"') // &quot;를 "로 변환
      .trim();

    this.commentInputValue = textContent;
    this.isCommentInputValid = true;
    this._isReplyCommentInputOpen = true;

    this._selectedMemo = data.memo;
    this._selectedReply = data.reply;

    if (this._mobileCommentInputElem?.nativeElement) {
      this._mobileCommentInputElem.nativeElement.readOnly = false;
      this._mobileCommentInputElem.nativeElement.focus();
      this._resetTextareaHeight();

      // 내용이 설정된 후 높이 조정
      setTimeout(() => {
        this._onCommentInputChange();
        this._updateNicknameIndent();
      }, 0);
    }
  }

  // 닉네임 인덴트 업데이트
  private _updateNicknameIndent(): void {
    if (this._nicknameElement?.nativeElement) {
      const width = this._nicknameElement.nativeElement.offsetWidth + 5;
      this._nicknameIndent = `${width}px`;
      this._cdr.detectChanges();
    } else {
      this._nicknameIndent = '0px';
    }
  }

  // 멘션 대상 가져오기
  public _getMentionTarget(): any {
    if (!this._isReplyCommentInputOpen) {
      return null;
    }

    if (this._commentState === 'comment' && this._selectedMemo) {
      // 첫 번째 답글인 경우 메모 작성자에게 멘션 (자신의 메모가 아닌 경우만)
      const isMine = this._selectedMemo.is_mine === 1;
      return isMine ? null : this._selectedMemo.author;
    } else if (this._commentState === 'subComment' && this._selectedComment) {
      // 답글의 답글인 경우 댓글 작성자에게 멘션 (자신의 댓글이 아닌 경우만)
      const isMine = this._selectedComment.is_mine === 1;
      return isMine ? null : this._selectedComment.author;
    } else if (this._commentState === 'subSubComment' && this._selectedReply) {
      // 답글의 답글의 답글인 경우 답글 작성자에게 멘션 (자신의 답글이 아닌 경우만)
      const isMine = this._selectedReply.is_mine === 1;
      return isMine ? null : this._selectedReply.author;
    }

    return null;
  }

  // 닉네임 인덴트 계산 (캐시된 값 반환)
  public _getNicknameIndent(): string {
    return this._nicknameIndent;
  }

  // limitHeight를 계산하는 헬퍼 메서드 추가
  public _shouldLimitHeight(memo: Memo): boolean {
    if (!memo.content) {
      return false;
    }

    const lines = memo.content.split('\n');

    // lines.length > 3 이거나 content.length > 64 인 경우에만 true
    return lines.length > 3 || memo.content.length > 64;
  }

  public trackByMemoId(_index: number, memo: Memo): string {
    return memo.id;
  }

  public _disableCommentInput(): void {
    if (this.isCommentInputValid) {
      this._eventBus.fire(
        'ReadingGroupMemoListComponent:clearTemporaryHighlight'
      );

      // 답글 입력 취소 이벤트 발생
      if (this._isReplyCommentInputOpen) {
        this._eventBus.fire(
          'ReadingGroupMemoListComponent:replyInputCancelled' as any
        );
      }

      this.isCommentInputValid = false;
      this.isCommentInputNotificationActive = false;
      this.annotationText = '';
      this.commentInputValue = '';
      this._resetTextareaHeight();

      // 포커스 해제
      if (this._commentInputElem?.nativeElement) {
        this._commentInputElem.nativeElement.readOnly = true;
        this._commentInputElem.nativeElement.blur();
      }
      if (this._mobileCommentInputElem?.nativeElement) {
        this._mobileCommentInputElem.nativeElement.readOnly = true;
        this._mobileCommentInputElem.nativeElement.blur();
      }
    }
  }

  public _onCloseCommentInputNotification(event: MouseEvent): void {
    event.stopPropagation();
    this.isCommentInputNotificationActive = false;
  }
}
